import { useLayoutEffect } from "react";

const REVEAL_DELAYS = [0, 80, 150, 220, 280, 340];

function isDecorative(node) {
  if (!(node instanceof Element)) return true;
  return (
    node.getAttribute("aria-hidden") === "true" ||
    node.classList.contains("pointer-events-none")
  );
}

function meaningfulChildren(node) {
  return Array.from(node.children || []).filter((child) => !isDecorative(child));
}

function revealTargetsFor(section) {
  let targets = meaningfulChildren(section);
  let depth = 0;

  // Most public sections use one geometry wrapper. Peel through at most two
  // single-child wrappers so the existing visual shell/background stays put
  // while its meaningful content reveals progressively.
  while (targets.length === 1 && depth < 2) {
    const nested = meaningfulChildren(targets[0]);
    if (!nested.length) break;
    targets = nested;
    depth += 1;
  }

  return targets.length ? targets : [section];
}

export function useProgressiveSectionReveal(rootRef, selector, observerOptions = {}) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const sections = Array.from(root.querySelectorAll(selector));
    if (!sections.length) return undefined;

    const reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    sections.forEach((section) => {
      section.classList.add("public-progressive-section");
      revealTargetsFor(section).forEach((target, index) => {
        target.classList.add("public-progressive-reveal-target");
        target.style.setProperty(
          "--public-reveal-delay",
          `${REVEAL_DELAYS[Math.min(index, REVEAL_DELAYS.length - 1)]}ms`
        );
      });
    });

    if (reducedMotion || !("IntersectionObserver" in window)) {
      sections.forEach((section) => section.classList.add("is-visible"));
      return () => {
        sections.forEach((section) => {
          section.classList.remove("public-progressive-section", "is-visible");
          revealTargetsFor(section).forEach((target) => {
            target.classList.remove("public-progressive-reveal-target");
            target.style.removeProperty("--public-reveal-delay");
          });
        });
      };
    }

    const observer = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: observerOptions.threshold ?? 0.2,
        rootMargin: observerOptions.rootMargin ?? "0px 0px -8% 0px",
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => {
      observer.disconnect();
      sections.forEach((section) => {
        section.classList.remove("public-progressive-section", "is-visible");
        revealTargetsFor(section).forEach((target) => {
          target.classList.remove("public-progressive-reveal-target");
          target.style.removeProperty("--public-reveal-delay");
        });
      });
    };
  }, [rootRef, selector, observerOptions.rootMargin, observerOptions.threshold]);
}
